import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { formatMoneyWithCurrency, formatPriceData } from '../../util/currencyHelpers';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { getCachedCountryFlag } from '../../util/countryFlagCache';
import { PiSteeringWheelFill } from 'react-icons/pi';
import { BsFuelPump } from 'react-icons/bs';
import { CiCalendarDate } from 'react-icons/ci';
import { IoSpeedometerOutline } from 'react-icons/io5';
// import { TbAutomaticGearbox } from "react-icons/tb";
import transmissionIcon from '../../assets/transmission.png';
import { FaRegHeart, FaHeart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { addFavorite, removeFavorite } from '../../ducks/user.duck';

import { AspectRatioWrapper, NamedLink, ResponsiveImage, CountryFlag } from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 7;
const MAX_TITLE_WORDS = 5; // Maximum number of words before truncation

// Helper function to truncate title to maximum word count
const truncateTitle = (title, maxWords) => {
  if (!title) return '';
  const words = title.split(' ');
  if (words.length <= maxWords) return title;
  return words.slice(0, maxWords).join(' ') + '...';
};

const priceData = (price, currency, intl) => {
  // Enhanced price formatting that supports ALL currencies
  if (price) {
    // Use our enhanced currency formatter that handles all currencies
    const { formattedPrice, priceTitle } = formatPriceData(price, intl);

    return { formattedPrice, priceTitle };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, badgeText } = props;
  const { listingType, priceReduction, isPOA } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  // Check if this is a POA (Price on Application) listing
  if (isPOA) {
    return (
      <div className={css.price}>
        <span className={css.priceValue}>POA</span>
      </div>
    );
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  // Check if this is a reduced price listing
  const isReduced = badgeText?.toLowerCase() === 'reduced' && priceReduction?.isActive;

  if (isReduced && priceReduction?.previousPrice) {
    // Show "was/now" pricing for reduced items
    const { formattedPrice: formattedPreviousPrice } = priceData(
      priceReduction.previousPrice,
      priceReduction.previousPrice.currency,
      intl
    );

    return (
      <div className={css.priceContainer}>
        <div className={css.priceStack}>
          <div className={css.previousPrice} title={`Previous price: ${formattedPreviousPrice}`}>
            {formattedPreviousPrice}
          </div>
          <div className={css.currentPrice} title={priceTitle}>
            {formattedPrice}
          </div>
        </div>
        {priceReduction?.reductionPercentage && (
          <div className={css.savingsText}>Save {priceReduction.reductionPercentage}%</div>
        )}
      </div>
    );
  } else {
    // Show normal pricing
    const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
    const pricePerUnit = isBookable ? (
      <span className={css.perUnit}>
        <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
      </span>
    ) : (
      ''
    );

    return (
      <div className={css.price} title={priceTitle}>
        {hasMultiplePriceVariants ? (
          <FormattedMessage
            id="ListingCard.priceStartingFrom"
            values={{ priceValue, pricePerUnit }}
          />
        ) : (
          <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
        )}
      </div>
    );
  }
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {string?} props.badgeText custom badge text (overrides auto-detection from listing level)
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector(state => state.user.currentUser);
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    badgeText,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData, geolocation } = currentListing.attributes;
  const listingLevel = publicData?.listing_level;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  // Carousel state and logic - handle both data structures
  let images = [];

  // Check if images are directly available (denormalized)
  if (currentListing.images && Array.isArray(currentListing.images)) {
    images = currentListing.images;
  }
  // Check if images are in relationships (normalized)
  else if (currentListing.relationships?.images?.data) {
    // This would need imageEntities to resolve, but for search results,
    // images should be denormalized, so this is fallback
    images = currentListing.relationships.images.data || [];
  }

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const currentImage = images.length > 0 ? images[currentImageIndex] : null;
  const hasMultipleImages = images.length > 1;

  // Check if listing is in user's favorites
  const favoriteListings = currentUser?.attributes?.profile?.privateData?.favoriteListings || [];
  const isFavorite = favoriteListings.includes(id);

  // Handle favorite toggle
  const handleFavoriteClick = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      history.push('/login');
      return false;
    }

    try {
      if (isFavorite) {
        dispatch(removeFavorite(id));
      } else {
        dispatch(addFavorite(id));
      }
    } catch (error) {
      console.error('Error in favorite toggle:', error);
    }

    return false;
  };

  // Carousel navigation functions
  const handlePrevImage = e => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = e => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Country flag functionality
  const [countryFlagData, setCountryFlagData] = React.useState(null);

  // Memoize the listing location data to prevent unnecessary re-renders
  const listingLocationData = React.useMemo(() => {
    if (!currentListing?.attributes) return null;
    return {
      address: currentListing.attributes.publicData?.location?.address,
      geolocation: currentListing.attributes.geolocation,
    };
  }, [
    currentListing?.attributes?.publicData?.location?.address,
    currentListing?.attributes?.geolocation,
  ]);

  React.useEffect(() => {
    let isMounted = true; // Flag to prevent setting state on unmounted component

    const fetchCountryFlag = async () => {
      try {
        const flagData = await getCachedCountryFlag(currentListing);
        if (isMounted) {
          setCountryFlagData(flagData);
        }
      } catch (error) {
        console.warn('Error fetching country flag:', error);
        if (isMounted) {
          setCountryFlagData(null);
        }
      }
    };

    // Only fetch if we have listing data and haven't already fetched
    if (currentListing && currentListing.id && listingLocationData) {
      fetchCountryFlag();
    } else {
      setCountryFlagData(null);
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [currentListing.id?.uuid, listingLocationData]); // Depend on memoized location data

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = currentImage
    ? Object.keys(currentImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Get badge CSS class based on listing level
  const getLevelBadgeClass = level => {
    if (!level) return null;

    const lowerLevel = level.toLowerCase();
    switch (lowerLevel) {
      case 'featured_level':
        return css.badgeFeatured;
      case 'standard_level':
        return css.badgeStandard;
      case 'premium_level':
        return css.badgePremium;
      case 'premium_pro_level':
        return css.badgePremiumPro;
      default:
        return css.badgeStandard; // Default styling
    }
  };

  // Get badge CSS class based on badge text (for custom badges)
  const getBadgeClassFromText = text => {
    if (!text) return css.badge;

    const lowerText = text.toLowerCase();
    switch (lowerText) {
      case 'new':
        return css.badgeNew;
      case 'featured':
        return css.badgeFeatured;
      case 'premium':
        return css.badgePremium;
      case 'premium-pro':
        return css.badgePremiumPro;
      case 'reduced':
        return css.badgeReduced;
      default:
        return css.badge; // Default green styling
    }
  };

  // Get display text for listing level
  const getLevelDisplayText = level => {
    if (!level) return null;

    const lowerLevel = level.toLowerCase();
    switch (lowerLevel) {
      case 'featured_level':
        return 'Featured';
      case 'standard_level':
        return 'Standard';
      case 'premium_level':
        return 'Premium';
      case 'premium_pro_level':
        return 'Premium Pro';
      default:
        return 'Standard';
    }
  };

  return (
    <div className={classes}>
      <div className={css.imageContainer}>
        <NamedLink name="ListingPage" params={{ id, slug }}>
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={currentImage}
              variants={variants}
              sizes={renderSizes}
            />
          </AspectRatioWrapper>
        </NamedLink>

        {/* Carousel navigation */}
        {hasMultipleImages && (
          <>
            <button
              className={css.prevButton}
              onClick={handlePrevImage}
              type="button"
              aria-label="Previous image"
            >
              <FaChevronLeft className={css.navIcon} />
            </button>
            <button
              className={css.nextButton}
              onClick={handleNextImage}
              type="button"
              aria-label="Next image"
            >
              <FaChevronRight className={css.navIcon} />
            </button>

            {/* Image indicators */}
            <div className={css.imageIndicators}>
              {images.map((_, index) => (
                <button
                  key={index}
                  className={classNames(css.indicator, {
                    [css.activeIndicator]: index === currentImageIndex,
                  })}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  type="button"
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Country flag positioned over image at bottom left */}
        {countryFlagData && (
          <div className={css.flagOverlay}>
            <CountryFlag
              flagData={countryFlagData}
              size="medium"
              showTooltip={true}
              showCountryName={false}
              className={css.overlayFlag}
            />
          </div>
        )}

        {/* Level badge */}
        {(badgeText || listingLevel) && (
          <div
            className={
              badgeText ? getBadgeClassFromText(badgeText) : getLevelBadgeClass(listingLevel)
            }
          >
            {badgeText || getLevelDisplayText(listingLevel)}
          </div>
        )}

        {publicData?.sold ? (
          <div className={css.badgeSold}>
            Sold
          </div>
        ) : publicData?.reserved ? (
          <div className={css.badgeReserved}>
            Reserved
          </div>
        ) : null}

        <button
          className={css.favoriteButton}
          onClick={handleFavoriteClick}
          onMouseDown={e => e.preventDefault()}
          onMouseUp={e => e.preventDefault()}
          type="button"
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorite ? (
            <FaHeart className={css.heartIcon} />
          ) : (
            <FaRegHeart className={css.heartIcon2} />
          )}
        </button>
      </div>
      <NamedLink name="ListingPage" params={{ id, slug }}>
        <div className={css.info}>
          <div className={css.title}>
            {richText(truncateTitle(title, MAX_TITLE_WORDS), {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          <div className={css.divider}></div>


          {/* First Row: Year, Transmission, Fuel */}
          <div className={css.detailsRowWithGap}>
            {publicData?.reg_year && (
              <div className={css.detailItem}>
                <CiCalendarDate className={css.icon} />
                <span className={css.detailText}>{publicData.reg_year}</span>
              </div>
            )}

            {publicData?.Transmission && (
              <div className={css.detailItem}>
                <img src={transmissionIcon} alt="Transmission" className={css.iconImage} />
                <span className={css.detailText}>{publicData.Transmission}</span>
              </div>
            )}

            {publicData?.Fuel && (
              <div className={css.detailItem}>
                <BsFuelPump className={css.icon2} />
                <span className={css.detailText}>{publicData.Fuel}</span>
              </div>
            )}
          </div>

          {/* Second Row: RHD/LHD + Mileage | Price (if not reduced) */}
          <div className={css.twoColumnRow}>
            <div className={css.leftColumn}>
              {publicData?.RHD_LHD && (
                <div className={css.detailItem}>
                  <PiSteeringWheelFill className={css.icon} />
                  <span className={css.detailText}>{publicData.RHD_LHD}</span>
                </div>
              )}

              {publicData?.Mileage && publicData?.miles_kms && (
                <div className={css.detailItem}>
                  <IoSpeedometerOutline className={css.icon2} />
                  <span className={css.detailText}>
                    <NumericFormat
                      value={publicData.Mileage}
                      displayType="text"
                      thousandSeparator=","
                    />{' '}
                    {publicData.miles_kms}
                  </span>
                </div>
              )}
            </div>

            {/* Show price in right column only if NOT reduced */}
            {!(badgeText?.toLowerCase() === 'reduced' && publicData?.priceReduction?.isActive) && (
              <div className={css.rightColumn}>
                <PriceMaybe
                  price={price}
                  publicData={publicData}
                  config={config}
                  intl={intl}
                  badgeText={badgeText}
                />
              </div>
            )}
          </div>

          {/* Third Row: Reduced Price (only if reduced) */}
          {badgeText?.toLowerCase() === 'reduced' && publicData?.priceReduction?.isActive && (
            <div className={css.priceReductionRow}>
              <PriceMaybe
                price={price}
                publicData={publicData}
                config={config}
                intl={intl}
                badgeText={badgeText}
              />
            </div>
          )}
          {/* {showAuthorInfo ? (
          <div className={css.authorInfo}>
            <FormattedMessage id="ListingCard.author" values={{ authorName }} />
          </div>
        ) : null} */}
        </div>
      </NamedLink>
    </div>
  );
};

export default ListingCard;
